import { QueryHookResult, useGenericQuery } from '../useGenericQuery'
import { queryKeys } from '../queryRegistry'
import { useApi } from 'api/ApiProvider';
import Api from 'axiosApi/api';
import { EmployeeTimeOffRegimeDetailDto } from 'axiosApi/models/employee-time-off-regime-detail-dto';
import { TimeOffType } from 'axiosApi/models/time-off-type';

export type TimeOffRegimeData = {
  id?: number;
  idEmployee?: number;
  idTimeOffRegime?: number;
  year?: number;
  isActive?: boolean;
  timeOffRegime?: any;
  employeeTimeOffStocks?: any[];
  employeeRegimeTimeOffTypes?: any[];
};

export type TimeOffTypeData = {
  id?: number;
  name?: string;
  label?: string;
  labelKey?: string;
  defaultDays?: number;
  isBillable?: boolean;
  isCumulative?: boolean;
  requiresSpecialApproval?: boolean;
  allowNegativeBalance?: boolean;
  validFrom?: string | Date | null;
  validUntil?: string | Date | null;
};

/**
 * Hook to fetch and cache employee's time off regime data
 * @param employeeId - The employee's ID
 * @param enabled - Whether the query should run
 */
export const useEmployeeTimeOffRegime = (
  employeeId: number,
  enabled: boolean = true
): QueryHookResult<TimeOffRegimeData> & { regimeId: number | null } => {
  const { timeoffApi }: Api = useApi();
  
  const result = useGenericQuery<TimeOffRegimeData>({
    queryKey: queryKeys.employeeTimeOffRegime(employeeId),
    queryFn: async (): Promise<TimeOffRegimeData[]> => {
      const response = await timeoffApi.apiVversionEmployeeTimeOffRegimeEmployeeEmployeeIdGet(
        employeeId,
        '1'
      );
      
      if (response?.status === 200 && response?.data?.data) {
        return [response.data.data];
      }
      
      return [];
    },
    enabled: enabled && !!employeeId
  });

  // Extract regime ID from the first result
  const regimeId = result.data.length > 0 ? result.data[0]?.id || null : null;

  return {
    ...result,
    regimeId
  };
};

/**
 * Hook to fetch and cache time off types
 * @param regimeId - The regime ID (for employee-specific types)
 * @param isHRView - Whether this is HR view (fetches all types)
 * @param enabled - Whether the query should run
 */
export const useTimeOffTypes = (
  regimeId?: number | null,
  isHRView: boolean = false,
  enabled: boolean = true
): QueryHookResult<TimeOffTypeData> => {
  const { timeoffApi }: Api = useApi();
  
  const result = useGenericQuery<TimeOffTypeData>({
    queryKey: queryKeys.timeOffTypes(regimeId, isHRView),
    queryFn: async (): Promise<TimeOffTypeData[]> => {
      let response;
      
      if (isHRView) {
        // Fetch all time off types for HR view
        response = await timeoffApi.apiVversionListTimeOffTypesByRegimeGet('1');
      } else if (regimeId) {
        // Fetch employee-specific time off types
        response = await timeoffApi.apiVversionListTimeOffTypesByEmployeeRegimeEmployeeRegimeIdGet(regimeId, '1');
      } else {
        // No regime ID yet, return empty
        return [];
      }
      
      if (response?.status === 200) {
        if (response.data?.data) {
          return response.data.data.queryResult || response.data.data || [];
        } else if (response.data) {
          return response.data.queryResult || response.data || [];
        }
      }
      
      return [];
    },
    enabled: enabled && (isHRView || !!regimeId)
  });

  return result;
};

